import { StatesModule } from '@uirouter/angular';
import { Transition } from '@uirouter/core';
import { DisplayService } from './services/display.service';

import { AccessGuardService } from '../auth/services/access-guard.service';
import { DISPLAY_ACCESS } from '../auth/types/access-level';
import { CompanyStateService } from '../auth/services/company-state.service';
import { BulkActivationComponent } from './components/bulk-activation/bulk-activation.component';
import { DisplayAddComponent } from './components/display-add/display-add.component';
import { DisplayDetailsComponent } from './components/display-details/display-details.component';
import { DisplayListComponent } from './components/display-list/display-list.component';
import { InstallComponent } from './components/install/install.component';
import { ScreenshotService } from './services/screenshot.service';
import { ViewportComponent } from './viewport/viewport.component';

export const routes: StatesModule = {
  states: [
    {
      name: 'apps.displays',
      abstract: true,
      component: ViewportComponent,
      resolve: [
        {
          token: 'requireDisplayAccess',
          deps: [Transition, AccessGuardService],
          resolveFn: (transition, accessGuardService: AccessGuardService) =>
            accessGuardService.requireDisplaysAccess(transition),
        },
      ],
    },
    {
      name: 'apps.displays.home',
      url: '/displays',
      redirectTo: 'apps.displays.list',
    },
    {
      name: 'apps.displays.list',
      url: '/displays/list/{folderId:any}',
      params: {
        folderId: null,
      },
      component: DisplayListComponent,
    },
    {
      name: 'apps.displays.change',
      url: '/displays/change/:displayId/:companyId',
      resolve: [
        {
          token: 'canAccessAps',
          deps: [Transition, CompanyStateService],
          resolveFn: (transition, companyStateService) => {
            var companyChangeRequired =
              companyStateService.getSelectedCompanyId() !==
              transition.params().companyId;

            return Promise.resolve()
              .then(() => {
                if (companyChangeRequired) {
                  return companyStateService.switchCompany(
                    transition.params().companyId,
                  );
                }
              })
              .then(function () {
                transition.abort();
                return transition.router.stateService.go(
                  'apps.displays.details',
                  {
                    displayId: transition.params().displayId,
                    cid: transition.params().companyId,
                    folderId: transition.params('from').folderId,
                  },
                );
              });
          },
        },
      ],
    },
    {
      name: 'apps.displays.add',
      url: '/displays/add',
      params: {
        schedule: null,
        folderId: null,
      },
      component: DisplayAddComponent,
      resolve: [
        {
          token: 'requireDisplayFullAccess',
          deps: [Transition, AccessGuardService],
          resolveFn: (transition, accessGuardService: AccessGuardService) =>
            accessGuardService.requireAccess(
              DISPLAY_ACCESS.FULL_ACCESS,
              transition,
            ),
        },
        {
          token: 'canAccessAps',
          deps: [
            Transition,
            DisplayService,
            ScreenshotService,
            'requireDisplayFullAccess',
          ],
          resolveFn: (transition, displayService, screenshotService) => {
            displayService.newDisplay();
            screenshotService.screenshot = {};

            if (transition.params().schedule) {
              displayService.setAssignedSchedule(transition.params().schedule);
            }
          },
        },
      ],
    },
    {
      name: 'apps.displays.bulk-activation',
      url: '/bulk-activation',
      component: BulkActivationComponent,
      resolve: [
        {
          token: 'requireDisplayFullAccess',
          deps: [Transition, AccessGuardService],
          resolveFn: (transition, accessGuardService: AccessGuardService) =>
            accessGuardService.requireAccess(
              DISPLAY_ACCESS.FULL_ACCESS,
              transition,
            ),
        },
      ],
    },
    {
      name: 'apps.displays.activate',
      url: '/activate/:activationKey',
      params: {
        activationKey: '',
      },
      component: DisplayAddComponent,
      resolve: [
        {
          token: 'requireDisplayFullAccess',
          deps: [Transition, AccessGuardService],
          resolveFn: (transition, accessGuardService: AccessGuardService) =>
            accessGuardService.requireAccess(
              DISPLAY_ACCESS.FULL_ACCESS,
              transition,
            ),
        },
        {
          token: 'canAccessAps',
          deps: [
            Transition,
            DisplayService,
            ScreenshotService,
            'requireDisplayFullAccess',
          ],
          resolveFn: (transition, displayService, screenshotService) => {
            displayService.newDisplay();
            displayService.display.activationKey =
              transition.params().activationKey;
            screenshotService.screenshot = {};
          },
        },
      ],
    },
    {
      name: 'apps.displays.install',
      url: '/install',
      component: InstallComponent,
      resolve: [
        {
          token: 'requireDisplayFullAccess',
          deps: [Transition, AccessGuardService],
          resolveFn: (transition, accessGuardService: AccessGuardService) =>
            accessGuardService.requireAccess(
              DISPLAY_ACCESS.FULL_ACCESS,
              transition,
            ),
        },
      ],
    },
    {
      name: 'apps.displays.details',
      url: '/displays/details/:displayId',
      params: {
        activated: false,
      },
      component: DisplayDetailsComponent,
    },
  ],
};
